import { NavLink, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  HomeIcon,
  UsersIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Medical', href: '/medical', icon: ShieldCheckIcon, badge: 'HIPAA' }, // HIPAA-compliant analytics
  { name: 'Errors', href: '/errors', icon: ExclamationTriangleIcon, badge: null }, // Badge will be dynamic
  { name: 'Database', href: '/database', icon: CircleStackIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon }
];

export default function SidebarComponent({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const { user } = useUser();

  return (
    <>
      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-50 h-screen w-72 transform bg-dark shadow-xl transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col overflow-y-auto">
          {/* Logo Header */}
          <div className="border-b border-gray-700 px-6 py-5.5 lg:py-6.5">
            <div className="flex items-center justify-between">
              <NavLink to="/" className="flex items-center gap-3">
                <span className="text-2xl">💧</span>
                <span className="text-xl font-bold text-white">PeeTrack Admin</span>
              </NavLink>

              {/* Close button for mobile */}
              <button
                className="text-gray-400 hover:text-white lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 lg:px-6">
            <div>
              <h3 className="mb-4 ml-2 text-sm font-semibold uppercase tracking-wider text-gray-400">
                Menu
              </h3>

              <ul className="space-y-2">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const Icon = item.icon;

                  return (
                    <li key={item.name}>
                      <NavLink
                        to={item.href}
                        className={`group relative flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-primary text-white shadow-lg'
                            : 'text-gray-300 hover:bg-dark-2 hover:text-white'
                        }`}
                        onClick={() => setSidebarOpen(false)} // Close sidebar on mobile after click
                      >
                        <Icon className="h-5 w-5" />
                        <span className="flex-1">{item.name}</span>

                        {/* Badge indicator (for errors/notifications or special labels) */}
                        {item.badge !== null && (
                          <>
                            {typeof item.badge === 'number' && item.badge > 0 && (
                              <span className="inline-flex items-center justify-center rounded-full bg-danger px-2 py-0.5 text-xs font-bold text-white">
                                {item.badge}
                              </span>
                            )}
                            {typeof item.badge === 'string' && (
                              <span className="inline-flex items-center justify-center rounded-full bg-green-600 px-2 py-0.5 text-xs font-bold text-white">
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}

                        {/* Active indicator */}
                        {isActive && (
                          <div className="absolute left-0 top-0 h-full w-1 rounded-r-lg bg-white" />
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          {/* User info at bottom */}
          <div className="border-t border-gray-700 px-6 py-4">
            <div className="flex items-center gap-3">
              <img
                src={user?.imageUrl || '/avatar-placeholder.png'}
                alt={user?.firstName || 'User'}
                className="h-10 w-10 rounded-full ring-2 ring-primary ring-opacity-50"
              />
              <div className="flex-1 overflow-hidden">
                <p className="truncate text-sm font-medium text-white">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-xs text-gray-400">
                  {user?.primaryEmailAddress?.emailAddress}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
